import { Select, SelectOption } from "@clockwise/design-system";
import { ConferencingType } from "@clockwise/schema";
import { MeetLogo } from "@clockwise/web-commons/src/components/svgs/MeetLogo";
import { TeamsLogo } from "@clockwise/web-commons/src/components/svgs/TeamsLogo";
import { ZoomLogo } from "@clockwise/web-commons/src/components/svgs/ZoomLogo";
import { routeToFeature } from "@clockwise/web-commons/src/util/routes.util";
import { SvgIconComponent, VideocamOffRounded } from "@material-ui/icons";
import { includes, map } from "lodash";
import React from "react";

const routeToZoomSetup = () =>
  routeToFeature({
    featureName: "appSettings",
    routerState: { kind: "window", target: "_blank" },
    options: {
      paramsObj: { highlight: "true" },
      hash: "#zoom",
      relative: true,
    },
  });

type VideoConfOption = {
  type: ConferencingType;
  label: string;
  Icon: SvgIconComponent;
};

type Props = {
  disabled: boolean;
  enabledTypes: ConferencingType[];
  onSelect: (type?: ConferencingType) => void;
  selected?: ConferencingType;
};

export const SelectVideoConf = ({ disabled, onSelect, enabledTypes, selected }: Props) => {
  const value = "" as const; // DEV NOTE: always keep select in "unselected" state
  const options: VideoConfOption[] = [];

  if (includes(enabledTypes, "ZOOM" as ConferencingType)) {
    options.push(ZoomOption);
  } else {
    options.push(ConnectZoomOption);
  }
  if (includes(enabledTypes, "MEET" as ConferencingType)) {
    options.push(MeetOption);
  }
  if (includes(enabledTypes, "TEAMS_FOR_BUSINESS" as ConferencingType)) {
    options.push(TeamsOption);
  }

  const handleChange = (option: ConferencingType | "None") => {
    if (option === selected) return;
    if (disabled) return;

    if (option === "None") {
      onSelect(undefined);
      return;
    }

    if (!includes(enabledTypes, option) && option === ConnectZoomOption.type) {
      routeToZoomSetup();
      return;
    }

    onSelect(option);
  };

  return (
    <Select disabled={disabled} onChange={handleChange} size="small" variant="inline" value={value}>
      <SelectOption value="">Add video call</SelectOption>
      {map(options, ({ type, label, Icon }) => (
        <SelectOption
          value={type}
          key={type}
          icon={Icon}
          iconProps={{ className: "cw-text-muted" }}
        >
          {label}
        </SelectOption>
      ))}
      <SelectOption
        value="None"
        icon={VideocamOffRounded}
        iconProps={{ className: "cw-text-muted" }}
      >
        No video conferencing
      </SelectOption>
    </Select>
  );
};

export const ZoomOption: VideoConfOption = {
  type: ConferencingType.ZOOM,
  label: "Add Zoom link",
  Icon: ZoomLogo,
};

export const ConnectZoomOption: VideoConfOption = {
  type: ConferencingType.ZOOM,
  label: "Connect Zoom account",
  Icon: ZoomLogo,
};

export const MeetOption: VideoConfOption = {
  type: ConferencingType.MEET,
  label: "Add Meet link",
  Icon: MeetLogo,
};

export const TeamsOption: VideoConfOption = {
  type: ConferencingType.TEAMS_FOR_BUSINESS,
  label: "Add Teams link",
  Icon: TeamsLogo,
};
